/**
 * Mostly Copilot generated (change whatever you like)
 * Dev-only profiler for NodeDataCache: tracks hit/miss, distinct design count, operation timings (get/set/flatten) and child ref sizes.
 * Disabled by default so overhead is a single boolean check when off.
 * Enabled in console via designer/client/src/ui/DesignerProfiling.tsx
 * */

export interface NodeDataCachePerfStats {
  hits: number;
  misses: number;
  sets: number;
  distinctDesigns: number;
  totalGetTimeMs: number;
  totalSetTimeMs: number;
  totalFlattenTimeMs: number;
  maxGetTimeMs: number;
  maxSetTimeMs: number;
  maxFlattenTimeMs: number;
  averageChildRefCount: number;
  maxChildRefCount: number;
  profilingEnabled: boolean;
}

class NodeDataCacheProfiler {
  enabled = false;
  private hits = 0;
  private misses = 0;
  private sets = 0;
  private distinctDesigns = 0;
  private totalGetTimeMs = 0;
  private totalSetTimeMs = 0;
  private totalFlattenTimeMs = 0;
  private maxGetTimeMs = 0;
  private maxSetTimeMs = 0;
  private maxFlattenTimeMs = 0;
  private totalChildRefCount = 0;
  private maxChildRefCount = 0;
  private flattenCalls = 0;
  private readonly designSeen = new WeakSet<object>();

  enable() {
    this.enabled = true;
  }
  disable() {
    this.enabled = false;
  }
  reset() {
    this.hits = 0;
    this.misses = 0;
    this.sets = 0;
    this.distinctDesigns = 0;
    this.totalGetTimeMs = 0;
    this.totalSetTimeMs = 0;
    this.totalFlattenTimeMs = 0;
    this.maxGetTimeMs = 0;
    this.maxSetTimeMs = 0;
    this.maxFlattenTimeMs = 0;
    this.totalChildRefCount = 0;
    this.maxChildRefCount = 0;
    this.flattenCalls = 0;
    // WeakSet cannot be cleared; recreate
    // (acceptable since only used when profiling enabled and after reset metrics start fresh)
    (this as unknown as { designSeen: WeakSet<object> }).designSeen = new WeakSet<object>();
  }

  recordGet(hit: boolean, durationMs: number) {
    if (!this.enabled) return;
    if (hit) {
      this.hits++;
    } else {
      this.misses++;
    }
    this.totalGetTimeMs += durationMs;
    if (durationMs > this.maxGetTimeMs) this.maxGetTimeMs = durationMs;
  }

  recordSet(durationMs: number, design: object) {
    if (!this.enabled) return;
    this.sets++;
    if (!this.designSeen.has(design)) {
      this.designSeen.add(design);
      this.distinctDesigns++;
    }
    this.totalSetTimeMs += durationMs;
    if (durationMs > this.maxSetTimeMs) this.maxSetTimeMs = durationMs;
  }

  recordFlatten(durationMs: number, refCount: number) {
    if (!this.enabled) return;
    this.totalFlattenTimeMs += durationMs;
    if (durationMs > this.maxFlattenTimeMs) this.maxFlattenTimeMs = durationMs;
    this.totalChildRefCount += refCount;
    if (refCount > this.maxChildRefCount) this.maxChildRefCount = refCount;
    this.flattenCalls++;
  }

  getStats(): NodeDataCachePerfStats {
    return {
      hits: this.hits,
      misses: this.misses,
      sets: this.sets,
      distinctDesigns: this.distinctDesigns,
      totalGetTimeMs: this.totalGetTimeMs,
      totalSetTimeMs: this.totalSetTimeMs,
      totalFlattenTimeMs: this.totalFlattenTimeMs,
      maxGetTimeMs: this.maxGetTimeMs,
      maxSetTimeMs: this.maxSetTimeMs,
      maxFlattenTimeMs: this.maxFlattenTimeMs,
      averageChildRefCount: this.flattenCalls ? this.totalChildRefCount / this.flattenCalls : 0,
      maxChildRefCount: this.maxChildRefCount,
      profilingEnabled: this.enabled,
    };
  }
}

export const nodeDataCacheProfiler = new NodeDataCacheProfiler();

export const enableNodeDataCacheProfiling = () => nodeDataCacheProfiler.enable();
export const disableNodeDataCacheProfiling = () => nodeDataCacheProfiler.disable();
export const resetNodeDataCachePerfStats = () => nodeDataCacheProfiler.reset();
export const getNodeDataCachePerfStats = (): NodeDataCachePerfStats => nodeDataCacheProfiler.getStats();
